import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DataService } from '../services/data.service';
import { LocationMappingService } from '../services/location-mapping.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-whatsapp-float',
  template: `
    <div class="whatsapp-float" *ngIf="show">
      <a [href]="whatsappLink" 
         target="_blank" 
         rel="noopener noreferrer"
         class="whatsapp-button"
         aria-label="Chat on WhatsApp">
        <i class="fa fa-whatsapp"></i>
        <span class="tooltip-text">{{ tooltipText }}</span>
      </a>
    </div>
  `,
  styles: [`
    .whatsapp-float {
      position: fixed;
      bottom: 80px;
      right: 20px;
      z-index: 9998;
      animation: bounce 2s infinite;
    }

    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-10px);
      }
      60% {
        transform: translateY(-5px);
      }
    }

    .whatsapp-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 60px;
      height: 60px;
      background: #25D366;
      color: white;
      border-radius: 50%;
      box-shadow: 0 4px 20px rgba(37, 211, 102, 0.5);
      text-decoration: none;
      transition: all 0.3s ease;
      position: relative;
    }

    .whatsapp-button:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 30px rgba(37, 211, 102, 0.7);
    }

    .whatsapp-button i {
      font-size: 32px;
    }

    .tooltip-text {
      visibility: hidden;
      width: 160px;
      background-color: #333;
      color: #fff;
      text-align: center;
      border-radius: 6px;
      padding: 8px;
      position: absolute;
      z-index: 1;
      right: 70px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 13px;
      white-space: nowrap;
      opacity: 0;
      transition: opacity 0.3s;
    }

    .tooltip-text::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 100%;
      margin-top: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: transparent transparent transparent #333;
    }

    .whatsapp-button:hover .tooltip-text {
      visibility: visible;
      opacity: 1;
    }

    /* Pulse effect */
    .whatsapp-button::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: #25D366;
      animation: pulse 1.5s infinite;
      z-index: -1;
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
        opacity: 0.7;
      }
      50% {
        transform: scale(1.3);
        opacity: 0;
      }
      100% {
        transform: scale(1);
        opacity: 0;
      }
    }

    @media (max-width: 768px) {
      .whatsapp-float {
        bottom: 70px;
        right: 15px;
      }

      .whatsapp-button {
        width: 50px;
        height: 50px;
      }

      .whatsapp-button i {
        font-size: 28px;
      }

      .tooltip-text {
        display: none;
      }
    }
  `]
})
export class WhatsappFloatComponent implements OnInit {
  show: boolean = false;
  whatsappLink: string = 'https://wa.me/918618958330?text=Hi%2C%20I%27m%20interested%20in%20NICT%20courses';
  tooltipText: string = 'Chat with us on WhatsApp';
  private branchList: any[] = [];
  private defaultNumber: string = '918618958330'; // Jayanagar head office

  constructor(
    private router: Router,
    private dataService: DataService,
    private locationMappingService: LocationMappingService
  ) {
    // Subscribe to route changes to update WhatsApp number dynamically
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateWhatsAppNumber();
    });
  }

  ngOnInit(): void {
    // Load branch data and show floater only after number is set
    this.dataService.getCachedBranches().subscribe(branches => {
      this.branchList = branches;
      this.updateWhatsAppNumber();
      
      // Show floater after number is determined (with small delay for UX)
      setTimeout(() => {
        this.show = true;
      }, 2000);
    });
  }

  private updateWhatsAppNumber(): void {
    // First priority: Get location from URL slug
    let location = this.getLocationFromUrl();
    
    // Second priority: Get location from localStorage (via LocationMappingService)
    if (!location) {
      location = this.locationMappingService.getStoredLocation();
    }
    
    if (location && this.branchList.length > 0) {
      // Find matching branch
      const branch = this.branchList.find(b => 
        b.location.toLowerCase() === location.toLowerCase()
      );
      
      if (branch && branch.mobile) {
        // Extract digits only from mobile number
        const mobileNumber = branch.mobile.replace(/\D/g, '');
        this.whatsappLink = `https://wa.me/${mobileNumber}?text=Hi%2C%20I%27m%20interested%20in%20NICT%20courses`;
        this.tooltipText = `Chat with ${branch.location} branch`;
        console.log(`WhatsApp number set for ${branch.location}: ${mobileNumber}`);
        return;
      }
    }
    
    // Fallback to default (head office) if no location found
    this.whatsappLink = `https://wa.me/${this.defaultNumber}?text=Hi%2C%20I%27m%20interested%20in%20NICT%20courses`;
    this.tooltipText = 'Chat with us on WhatsApp';
    console.log('WhatsApp number set to default (Head Office)');
  }

  private getLocationFromUrl(): string | null {
    // Get current URL and extract location slug
    const url = this.router.url;
    const segments = url.split('/').filter(s => s && !s.includes('?'));
    
    // Check if first segment is a location slug
    if (segments.length > 0) {
      const potentialSlug = segments[0];
      const location = this.locationMappingService.getLocationFromSlug(potentialSlug);
      if (location) {
        return location;
      }
    }
    
    return null;
  }
}
